import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Surface } from '@americanexpress/dls-react';
import { useMediaBreakpoint } from '@americanexpress/consumer-travel-base';
import { useDateSelector } from '../../context/dateContext';
import {
  getEndDate,
  getIsDateRange,
  getMinShownDateString,
  getStartDate,
} from '../../selectors/dateSelectors';
import { useTimeSelector } from '../../context/timeContext';
import {
  getEndTime, getHasTimeDropdown, getStartTime, getNewLabelEnabled,
} from '../../selectors/timeSelectors';
import { setEndDate, setStartDate } from '../../ducks/dateDuck';
import { setEndTime, setStartTime } from '../../ducks/timeDuck';
import CalendarHorizontal from '../CalendarHorizontal/CalendarHorizontal';
import CalendarFooter from '../CalendarFooter/CalendarFooter';
import CalendarVertical from '../CalendarVertical/CalendarVertical';
import styles from './InlineCalendar.scss';
import DaysOfTheWeek from '../DaysOfTheWeek/DaysOfTheWeek';
import DateTimeField from '../DateTimeField/DateTimeField';
import { convertDefaultFormatStringDateToLocaleFormat } from '../../utils/dateUtils';
import { getTimeLabels } from '../../utils/timeUtils';

const InlineCalendar = ({ placeholder, onClose }) => {
  const startDate = useDateSelector(getStartDate);
  const endDate = useDateSelector(getEndDate);
  const startDateLocalFormat = convertDefaultFormatStringDateToLocaleFormat(startDate);
  const endDateLocalFormat = convertDefaultFormatStringDateToLocaleFormat(endDate);
  const isDateRange = useDateSelector(getIsDateRange);
  const minShownDate = useDateSelector(getMinShownDateString);
  const isMobile = useMediaBreakpoint('sm-down');
  const hasTimeDropdown = useTimeSelector(getHasTimeDropdown);
  const startTime = useTimeSelector(getStartTime);
  const endTime = useTimeSelector(getEndTime);
  const isEnabledNewTimeLabel = useTimeSelector(getNewLabelEnabled);
  const timeLabels = getTimeLabels(isEnabledNewTimeLabel);

  const { formatMessage } = useIntl();

  return (
    <Surface variant="foreground" data-testid="date-picker-inline-surface" className="dls-white-bg margin-1-lr">
      {startDate && (
        <div aria-live="assertive" className="sr-only">
          {formatMessage({ id: 'aria.start_date_update' }, { startDateLocalFormat })}
          {startTime && hasTimeDropdown && <span id="startTime"> {startTime} </span>}
        </div>
      )}
      {endDate && (
        <div aria-live="assertive" className="sr-only">
          {formatMessage({ id: 'aria.end_date_update' }, { endDateLocalFormat })}
          {endTime && hasTimeDropdown && <span id="endTime"> {endTime} </span>}
        </div>
      )}
      <div className={clsx(isMobile && hasTimeDropdown ? 'col-sm-12' : 'flex flex-justify-center pad-1-t flex-wrap')}>
        <DateTimeField
          shownDate={minShownDate}
          placeholder={placeholder.startDate}
          onChangeInput={setStartDate}
          onChangeTime={setStartTime}
          stateKey="start"
          hasTimeDropdown={hasTimeDropdown}
          timeAriaLabel={formatMessage({ id: timeLabels.timeAriaLabelForDeparture })}
          className={clsx(!isMobile && 'flex flex-justify-end', isMobile && hasTimeDropdown ? 'flex flex-wrap margin-1-t' : 'col-sm-6')}
        />
        {isDateRange && (
          <>
            <DateTimeField
              shownDate={startDate}
              placeholder={placeholder.endDate}
              onChangeInput={setEndDate}
              onChangeTime={setEndTime}
              stateKey="end"
              hasTimeDropdown={hasTimeDropdown}
              timeAriaLabel={formatMessage({ id: timeLabels.timeAriaLabelForArrival })}
              className={clsx(!isMobile && 'flex flex-align-items-start', isMobile && hasTimeDropdown ? 'flex flex-wrap margin-1-t' : 'col-sm-6')}
            />
            <div className="hidden" id="date-picker-field-description">
              Enter a date or use the up and down arrow keys to change the current date.
            </div>
          </>
        )}
      </div>

      {isMobile && (
        <div className={clsx('pad-1', styles.daysOfWeek)}>
          <DaysOfTheWeek />
        </div>
      )}

      {minShownDate
        && (
          <div aria-hidden="true" className="flex flex-justify-center">
            {isMobile ? <CalendarVertical /> : <CalendarHorizontal />}
          </div>
        )}
      <div className={clsx(styles.inlineFooter, 'margin-center')}>
        <CalendarFooter onClose={onClose} isInline={true} />
      </div>
    </Surface>
  );
};

InlineCalendar.propTypes = {
  placeholder: PropTypes.shape({
    endDate: PropTypes.string,
    startDate: PropTypes.string,
  }),
  onClose: PropTypes.func,
};

export default InlineCalendar;
